import { useEffect, useState } from "react";
import { API } from "@/lib/api";
import axios from "axios";
import { Download } from "lucide-react";

export default function AdminPage() {
  const [signups, setSignups] = useState([]);
  const [messages, setMessages] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tab, setTab] = useState("payments");

  useEffect(() => {
    axios.get(`${API}/admin/demo-payments`).then((r) => setPayments(r.data)).catch(() => {});
    axios.get(`${API}/admin/demo-signups`).then((r) => setSignups(r.data)).catch(() => {});
    axios.get(`${API}/admin/contact-messages`).then((r) => setMessages(r.data)).catch(() => {});
  }, []);

  const csvUrl = tab === "payments"
    ? `${API}/admin/demo-payments.csv`
    : tab === "signups"
    ? `${API}/admin/demo-signups.csv`
    : `${API}/admin/contact-messages.csv`;

  return (
    <div data-testid="admin-page" className="mx-auto max-w-7xl px-6 lg:px-10 py-14">
      <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">Admin</h1>
      <p className="text-slate-600 mt-2 text-sm">Live view of demo payments, signups, and contact messages. Download any tab as CSV for Excel.</p>

      <div className="mt-8 flex gap-2 border-b border-slate-200 flex-wrap">
        <button data-testid="admin-tab-payments" onClick={() => setTab("payments")} className={`px-4 py-2 text-sm ${tab === "payments" ? "border-b-2 border-[#0052FF] text-slate-950 font-medium" : "text-slate-600"}`}>Demo Payments ({payments.length})</button>
        <button data-testid="admin-tab-signups" onClick={() => setTab("signups")} className={`px-4 py-2 text-sm ${tab === "signups" ? "border-b-2 border-[#0052FF] text-slate-950 font-medium" : "text-slate-600"}`}>Demo Signups ({signups.length})</button>
        <button data-testid="admin-tab-messages" onClick={() => setTab("messages")} className={`px-4 py-2 text-sm ${tab === "messages" ? "border-b-2 border-[#0052FF] text-slate-950 font-medium" : "text-slate-600"}`}>Contact Messages ({messages.length})</button>
      </div>

      <div className="mt-6 flex justify-end">
        <a href={csvUrl} data-testid="admin-download-csv" className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 text-sm hover:bg-slate-700 transition-colors">
          <Download className="w-4 h-4" /> Download CSV
        </a>
      </div>

      <div className="mt-4 overflow-x-auto border border-slate-200">
        {tab === "payments" && (
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left">
                <th className="p-3">Date</th><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Phone</th>
                <th className="p-3">Education</th><th className="p-3">Experience</th><th className="p-3">Course</th>
                <th className="p-3">Demo date</th><th className="p-3">Amount</th><th className="p-3">UTR</th><th className="p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-t border-slate-200">
                  <td className="p-3">{new Date(p.created_at).toLocaleString()}</td>
                  <td className="p-3">{p.full_name}</td>
                  <td className="p-3">{p.email}</td>
                  <td className="p-3">{p.phone}</td>
                  <td className="p-3">{p.education}</td>
                  <td className="p-3">{p.experience}</td>
                  <td className="p-3">{p.course_interest}</td>
                  <td className="p-3">{p.demo_date || "-"}</td>
                  <td className="p-3">₹{p.amount_inr}</td>
                  <td className="p-3 font-mono text-xs">{p.upi_reference}</td>
                  <td className="p-3"><span className="text-xs px-2 py-0.5 bg-amber-50 text-amber-800 border border-amber-200">{p.status}</span></td>
                </tr>
              ))}
              {payments.length === 0 && <tr><td colSpan={11} className="p-6 text-center text-slate-500">No demo payments yet.</td></tr>}
            </tbody>
          </table>
        )}
        {tab === "signups" && (
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left"><th className="p-3">Date</th><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Phone</th><th className="p-3">Course</th><th className="p-3">Slot</th><th className="p-3">Experience</th></tr>
            </thead>
            <tbody>
              {signups.map((s) => (
                <tr key={s.id} className="border-t border-slate-200"><td className="p-3">{new Date(s.created_at).toLocaleString()}</td><td className="p-3">{s.full_name}</td><td className="p-3">{s.email}</td><td className="p-3">{s.phone}</td><td className="p-3">{s.course_interest}</td><td className="p-3">{s.preferred_time}</td><td className="p-3">{s.experience_level || "-"}</td></tr>
              ))}
              {signups.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-500">No demo signups yet.</td></tr>}
            </tbody>
          </table>
        )}
        {tab === "messages" && (
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left"><th className="p-3">Date</th><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Phone</th><th className="p-3">Subject</th><th className="p-3">Message</th></tr>
            </thead>
            <tbody>
              {messages.map((m) => (
                <tr key={m.id} className="border-t border-slate-200"><td className="p-3">{new Date(m.created_at).toLocaleString()}</td><td className="p-3">{m.full_name}</td><td className="p-3">{m.email}</td><td className="p-3">{m.phone || "-"}</td><td className="p-3">{m.subject}</td><td className="p-3 max-w-md">{m.message}</td></tr>
              ))}
              {messages.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">No contact messages yet.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
